import React, { useState, useMemo } from "react";
import api from "@/lib/api";
import { Printer, Download, Calendar, Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ermittelt den Montag der Kalenderwoche für ein ISO-Datum
const mondayOf = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // 0 = Montag
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function Plakat() {
  const today = new Date().toISOString().slice(0, 10);
  const [weekStart, setWeekStart] = useState(mondayOf(today));
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  }, [weekStart]);

  // Lade PDF authentifiziert als Blob → Object-URL für iframe.
  // (Iframe kann keine Bearer-Header senden → deshalb Blob-Trick.)
  React.useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setError(null);
    // cache-buster verhindert dass Browser/CDN eine alte PDF liefert
    api.get(`/admin/poster/week?week_start=${weekStart}&_r=${reloadKey}&t=${Date.now()}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        setPdfUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setError("Plakat konnte nicht geladen werden — bitte neu einloggen."); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [weekStart, reloadKey]);

  const shiftWeek = (dir) => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/poster/week?week_start=${weekStart}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `programm-${weekStart}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Plakat heruntergeladen");
    } catch {
      toast.error("Download fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="plakat-page">
      <div className="flex items-center gap-3">
        <Printer size={22} className="text-[#D4AF37]" />
        <h1 className="font-heading text-3xl">Wochen-Plakat · A3 Druck</h1>
      </div>

      <div className="card-lux p-5">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mb-4">Kalenderwoche wählen</div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => shiftWeek(-1)} data-testid="plakat-prev-week"
            className="px-3 py-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-sm">←</button>

          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/30 rounded bg-[#D4AF37]/[0.05]">
            <Calendar size={14} className="text-[#D4AF37]" />
            <input type="date" value={weekStart}
              onChange={(e) => setWeekStart(mondayOf(e.target.value))}
              data-testid="plakat-week-input"
              className="bg-transparent outline-none text-sm font-mono" />
            <span className="text-[10px] text-[#575148]">→</span>
            <span className="text-sm font-mono text-[#EEDC82]">{fmt(weekEnd)}</span>
          </div>

          <button onClick={() => shiftWeek(+1)} data-testid="plakat-next-week"
            className="px-3 py-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-sm">→</button>

          <button onClick={() => setReloadKey((k) => k + 1)} data-testid="plakat-refresh"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37] text-[11px] uppercase tracking-widest">
            <RefreshCw size={12} /> Aktualisieren
          </button>

          <button onClick={download} disabled={busy} data-testid="plakat-download"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-5 py-2 rounded text-[11px] uppercase tracking-widest disabled:opacity-50">
            <Download size={13} /> {busy ? "…" : "PDF herunterladen"}
          </button>
        </div>

        <div className="mt-3 text-xs text-[#A8A398]">
          Enthält alle Vorstellungen von <b className="text-[#F5F0E6]">{fmt(weekStart)}</b> bis <b className="text-[#F5F0E6]">{fmt(weekEnd)}</b> · abgesagte &amp; private ausgeschlossen · A3 · QR-Code zum Programm.
        </div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#1A1A1A] text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
          <Eye size={12} /> Live-Vorschau
        </div>
        <div className="bg-[#050505] p-4">
          {error ? (
            <div className="w-full h-[85vh] flex items-center justify-center text-[#EE9CA1] text-sm bg-[#0A0A0A] rounded border border-[#5B1319]/40">{error}</div>
          ) : pdfUrl ? (
            <iframe key={reloadKey} src={pdfUrl} title="Wochen-Plakat" data-testid="plakat-iframe"
              className="w-full h-[85vh] rounded border border-[#1A1A1A] bg-[#0A0A0A]" />
          ) : (
            <div className="w-full h-[85vh] flex items-center justify-center text-[#A8A398] text-sm bg-[#0A0A0A] rounded">Lade Plakat …</div>
          )}
        </div>
      </div>
    </div>
  );
}
